/**
 * NO localStorage reads or writes happen in this file.
 * All reads/writes use in-memory store with optional API write-through.
 */

// DEV-963: was `import type { GUID } from "../Collections/types"` — that
// module was removed in an earlier reorg. GUID is just a string alias,
// declared locally here to keep this file's GUID usages working without
// taking a dependency on a moving import path.
type GUID = string;

// "Primary Location" is stored as a Community Entity GUID.
// KPIGridTable grouping relies on derived *name* fields community_l1/l2/l3 (and Community)
// that live directly on the project object. When primary_location_entity_guid changes, we
// recompute those derived name fields so grouping updates immediately.
// DEV-932 Phase 7: was flatfile imports from Data/Security/. The
// derived-name compute below now starts from empty arrays — when
// primary_location_entity_guid changes, the recompute returns no
// derived names UNTIL the caller supplies fresh entity data. Filed
// as follow-up: thread useApiCommunityData into the caller chain so
// the recompute has live data. For now, the project's
// community_l1/l2/l3 fields fall back to whatever the API returned
// on the project row itself.
const communityEntities: any[] = [];
const communityEntityRelationships: any[] = [];
import { projectConfigStore, isProjectConfigHydrated } from "../store";
import { updateProject } from "../../api/projects";

// Project configuration overrides are stored in the in-memory store
// and persisted to PostgreSQL via API write-through.
//
// IMPORTANT:
// - This layer ONLY covers *project configuration* (name/desc/owner/location/visibility/schema/profile/etc.)
// - It must NOT be used for ACL / collection memberships / community roles.

/**
 * The project visibility ladder — DEV-1463, migration 275.
 *
 * THE PROJECT STANDARD (Skip, 2026-07-13): "the mini project approach is what I
 * want to implement on full projects so we have a project standard." Full
 * projects now use the mini-project value space verbatim. Ordinal:
 *
 *      team_only (0)  <  community (1)  <  public (2)
 *
 * "public" = any logged-in L4E user. NOT anonymous web visitors (migration 165's
 * semantics, inherited) — the anonymous case is a separate, later decision.
 *
 * SUPERSEDES the DEV-1039 pair (`Visibility` = public|private, plus
 * `ProjectPublicScope` = platform|within_community), which encoded THE SAME
 * LADDER across two fields:
 *
 *      private                    ->  team_only
 *      public + within_community  ->  community
 *      public + platform          ->  public
 *
 * Not a competing design — the same design, discovered twice. The old pair is
 * retired below.
 */
export type ProjectVisibility = "team_only" | "community" | "public";

/** Ordinal for the cap comparison (progress_visibility <= visibility). */
export const PROJECT_VISIBILITY_ORDINAL: Record<ProjectVisibility, number> = {
  team_only: 0,
  community: 1,
  public: 2,
};

/**
 * @deprecated DEV-1463 — the two-value model. Retired in favour of
 * {@link ProjectVisibility}. Kept only because `src/types/security.ts` exports
 * an identically-named type that the V2 permission evaluator still speaks; that
 * reconciliation is DEV-1071's. Do not use for new work.
 */
export type Visibility = "public" | "private";

/**
 * @deprecated DEV-1463 — folded into {@link ProjectVisibility} losslessly
 * (`within_community` -> `community`, `platform` -> `public`). Do not use.
 */
export type ProjectPublicScope = "platform" | "within_community";

export type ProjectOwnerType = "user" | "community";

/**
 * ProjectConfigOverride is a partial overlay applied on top of seed project records.
 *
 * NOTE on backward compatibility:
 * Earlier iterations stored `owning_community_entity_guid`. We still read/apply that
 * value, but new work should prefer owner_type/owner_guid.
 */
export type ProjectConfigOverride = {
  project_guid: GUID;

  // Identity / presentation
  project_name?: string;
  description?: string;
  project_start_date?: string;
  project_end_date?: string;

  // Optional: disable specific workflow types for this project.
  // If disabled, actions are applied immediately (no WorkflowRequest).
  disabled_workflow_action_types?: string[];
  // NOTE: legacy prototype fields (kept for backward compatibility)
  status?: string;
  size?: string;

  // Standard project meta (always present)
  project_category?: string;
  project_status?: string;
  content_status?: string;

  // Ownership (governance)
  owner_type?: ProjectOwnerType;
  owner_guid?: GUID;

  // Back-compat (deprecated)
  owning_community_entity_guid?: GUID;

  // Primary location (where the work happens)
  primary_location_entity_guid?: GUID;

  // Optional map point override (WGS84)
  location_lat?: string;
  location_lon?: string;

  // ── Visibility (DEV-1463 — the project standard; migration 275) ──
  // Three-value ladder, mirroring mini-projects. Persisted to
  // projects.visibility via the API write-through below.
  visibility?: ProjectVisibility;

  /**
   * Independent visibility for Progress/Management data, CAPPED at
   * `visibility` (progress <= visibility). Mirrors mini-projects: progress is
   * the team's working state unless the owner deliberately opens it up as a
   * transparency signal.
   */
  progress_visibility?: ProjectVisibility;

  /** @deprecated DEV-1463 — folded into `visibility`. Read-only legacy. */
  public_scope?: ProjectPublicScope;

  /**
   * Hard override: when true, the project is hidden from everyone except
   * project Owner/Admin (owner_admin) users. This overrides Public/Private.
   */
  is_hidden?: boolean;

  // Config selections
  metadata_schema_guid?: string;
  kpi_profile_guid?: string;

  // Optional media
  logo_url?: string;
  tile_image_url?: string;
  /** Card-specific hero image (optimized thumbnail for project cards). Falls back to hero_image_url when unset. */
  card_image_url?: string;

  tags?: string[];

  // Future (silent support): parent/child programs (no UI yet)
  parent_project_guid?: GUID;

  /**
   * When true, enables audit trail features for this project:
   * - Baseline lock becomes available
   * - Import versioning preserves history (soft-delete instead of hard-delete)
   * - Increases storage footprint
   * Defaults to false (Phases 1-3 hard-delete behavior).
   */
  audit_enabled?: boolean;

  /**
   * Official reporting cadence for this project (legacy flat string).
   * Controls Summary tab period boundaries and reporting-period filters.
   * Defaults to "monthly" if unset.
   * @deprecated Prefer `reporting_schedule` for new code. This field is kept
   * for backward compat — the UI writes both fields on save.
   */
  reporting_cadence?: "daily" | "weekly" | "bi-weekly" | "monthly" | "quarterly" | "semi-annual" | "annual";

  /**
   * Structured reporting schedule for this project.
   * Provides cadence + interval + day-of-week / monthly-rule detail,
   * enabling the reliability engine to compute exact due dates.
   */
  reporting_schedule?: {
    cadence: "daily" | "weekly" | "monthly" | "quarterly";
    interval?: number;
    by_weekday?: number[];
    monthly_mode?: "day" | "first" | "last";
    monthly_day_of_month?: number;
    monthly_weekday?: number;
  };

  /**
   * Anchor date for Gantt forward scheduling.
   * Falls back to project_start_date when unset.
   */
  schedule_start_date?: string;

  /**
   * Gantt chart color theme.
   * "black" = dark bars, "white" = light bars, "match_theme" = use design tokens.
   * Defaults to "match_theme" if unset.
   */
  gantt_color_theme?: "black" | "white" | "match_theme";

  /**
   * Working days of the week (ISO 8601: 1=Mon..7=Sun).
   * null = 7-day calendar (calendar days = working days, backward-compatible).
   * Default 5-day week: [1,2,3,4,5]. Middle East: [7,1,2,3,4] (Sun-Thu).
   * DEV-563.
   */
  work_week?: number[] | null;

  /**
   * Total planned budget for this project (numeric).
   * Used by the Budget thermometer to compute percentage-of-budget view.
   * DEV-576.
   */
  budget_estimate?: string;

  /**
   * ISO 4217 currency code for budget tracking (e.g. "USD", "EUR", "SEK").
   * Defaults to "USD" when unset.
   * DEV-576.
   */
  budget_currency?: string;

  /**
   * Contingency buffer percentage applied to budget calculations.
   * Defaults to 10 when unset.
   */
  contingency_percent?: number;
};

type OverrideMap = Record<string, Omit<ProjectConfigOverride, "project_guid">>;

function ensureWarm(): void {
  // No-op: localStorage warm-start removed. Data comes from API hydration only.
}

export const getProjectOverrides = (): OverrideMap => {
  ensureWarm();
  const result: OverrideMap = {};
  projectConfigStore.forEach((val, key) => {
    const { project_guid: _ignored, ...rest } = val as any;
    result[key] = rest;
  });
  return result;
};

export const getProjectOverride = (project_guid: GUID): ProjectConfigOverride | null => {
  ensureWarm();
  const g = String(project_guid);
  const gUp = g.toUpperCase();

  // Try exact key match first
  let val = projectConfigStore.get(gUp);
  if (!val) {
    // Try case-insensitive scan
    let foundKey: string | undefined;
    projectConfigStore.forEach((_, key) => {
      if (key.toUpperCase() === gUp) foundKey = key;
    });
    if (foundKey) val = projectConfigStore.get(foundKey);
  }

  if (!val) return null;
  return { project_guid, ...(val as any) };
};

export const setProjectOverride = (project_guid: GUID, patch: Partial<ProjectConfigOverride>): void => {
  ensureWarm();
  const guid = String(project_guid);
  const guidUp = guid.toUpperCase();

  // Case-insensitive key resolution: find existing key if it exists
  let existingKey = guidUp;
  projectConfigStore.forEach((_, key) => {
    if (key.toUpperCase() === guidUp) existingKey = key;
  });

  const existing = projectConfigStore.get(existingKey) || {};
  const { project_guid: _ignored, ...rest } = (patch as any) || {};

  const updated = {
    ...existing,
    ...rest,
  };

  projectConfigStore.set(guidUp, updated);

  try {
    window.dispatchEvent(new CustomEvent("l4e_project_config_changed"));
  } catch {
    // ignore
  }

  // Write-through to API
  if (patch && Object.keys(rest).length > 0) {
    // Map frontend snake_case fields to API camelCase fields
    const apiPatch: Record<string, any> = { ...rest };

    /* ── DEV-1463 visibility (the project standard) ──────────────────────────
     *
     * `visibility` and `progress_visibility` already speak the canonical
     * three-value ladder (team_only|community|public) — ProjectConfigModal was
     * rebuilt against it — so they pass straight through to the API, which
     * validates them and enforces the cap (progress <= visibility, with a silent
     * auto-downgrade). No translation needed.
     *
     * ⚠ Historical hazard, now closed: before migration 275,
     *   `UpdateProjectRequest` had no `Visibility` field, so this write-through
     *   happily sent the old DEV-1039 `"public" | "private"` and ASP.NET
     *   silently DROPPED it — which is why that setting never persisted across a
     *   reload. Adding the field made the API start listening, and "private" is
     *   not in the value space, so a save on Private would have 400'd. The
     *   modal now emits the canonical values, so the hazard is gone at source.
     *
     * `public_scope` is retired — folded into the ladder losslessly
     * (within_community -> community, platform -> public). It is not a column on
     * `projects`; never send it.
     */
    delete apiPatch.public_scope;

    if ('progress_visibility' in apiPatch) {
      apiPatch.progressVisibility = apiPatch.progress_visibility;
      delete apiPatch.progress_visibility;
    }

    if ('tile_image_url' in apiPatch) {
      apiPatch.heroImageUrl = apiPatch.tile_image_url;
      delete apiPatch.tile_image_url;
    }
    if ('card_image_url' in apiPatch) {
      apiPatch.cardImageUrl = apiPatch.card_image_url;
      delete apiPatch.card_image_url;
    }
    if ('logo_url' in apiPatch) {
      apiPatch.logoUrl = apiPatch.logo_url;
      delete apiPatch.logo_url;
    }
    if ('primary_location_entity_guid' in apiPatch) {
      apiPatch.primaryLocationEntityGuid = apiPatch.primary_location_entity_guid;
      delete apiPatch.primary_location_entity_guid;
    }
    if ('location_lat' in apiPatch) {
      apiPatch.locationLat = apiPatch.location_lat;
      delete apiPatch.location_lat;
    }
    if ('location_lon' in apiPatch) {
      apiPatch.locationLon = apiPatch.location_lon;
      delete apiPatch.location_lon;
    }
    if ('project_name' in apiPatch) {
      apiPatch.name = apiPatch.project_name;
      delete apiPatch.project_name;
    }
    if ('project_category' in apiPatch) {
      apiPatch.projectCategory = apiPatch.project_category;
      delete apiPatch.project_category;
    }
    if ('project_status' in apiPatch) {
      apiPatch.projectStatus = apiPatch.project_status;
      delete apiPatch.project_status;
    }
    if ('content_status' in apiPatch) {
      apiPatch.contentStatus = apiPatch.content_status;
      delete apiPatch.content_status;
    }
    if ('project_start_date' in apiPatch) {
      apiPatch.startDate = apiPatch.project_start_date;
      delete apiPatch.project_start_date;
    }
    if ('project_end_date' in apiPatch) {
      apiPatch.finishDate = apiPatch.project_end_date;
      delete apiPatch.project_end_date;
    }

    // Also persist community_l1/l2/l3 derived from primary location
    // so the grouping fields survive a reload.
    if ('primary_location_entity_guid' in (rest as any)) {
      const locGuid = String((rest as any).primary_location_entity_guid ?? "").trim();
      if (locGuid) {
        const names = resolveCommunityPathNames(locGuid);
        if (names.length > 0) {
          apiPatch.communityL1 = names[0] ?? "";
          apiPatch.communityL2 = names[1] ?? "";
          apiPatch.communityL3 = names[2] ?? "";
          apiPatch.communityDisplay = names[names.length - 1] ?? "";
        }
      }
    }

    if ('reporting_cadence' in apiPatch) {
      apiPatch.reportingCadence = apiPatch.reporting_cadence;
      delete apiPatch.reporting_cadence;
    }

    if ('schedule_start_date' in apiPatch) {
      apiPatch.scheduleStartDate = apiPatch.schedule_start_date;
      delete apiPatch.schedule_start_date;
    }

    if ('gantt_color_theme' in apiPatch) {
      apiPatch.ganttColorTheme = apiPatch.gantt_color_theme;
      delete apiPatch.gantt_color_theme;
    }

    if ('work_week' in apiPatch) {
      apiPatch.workWeek = apiPatch.work_week;
      delete apiPatch.work_week;
    }

    // DEV-814: category_weights is an object on the frontend, JSON string
    // on the wire (the backend stores it as jsonb but the DTO accepts a
    // string and persists it verbatim).
    if ('category_weights' in apiPatch) {
      const cw = apiPatch.category_weights;
      apiPatch.categoryWeights = typeof cw === 'string' ? cw : JSON.stringify(cw);
      delete apiPatch.category_weights;
    }

    if ('budget_estimate' in apiPatch) {
      apiPatch.budgetEstimate = apiPatch.budget_estimate;
      delete apiPatch.budget_estimate;
    }
    if ('budget_currency' in apiPatch) {
      apiPatch.budgetCurrency = apiPatch.budget_currency;
      delete apiPatch.budget_currency;
    }
    if ('contingency_percent' in apiPatch) {
      apiPatch.contingencyPercent = apiPatch.contingency_percent;
      delete apiPatch.contingency_percent;
    }

    // DEV-916 — Metadata schema selection persistence.
    // The Project Settings modal puts the selected schema GUID into the
    // patch as `metadata_schema_guid`. The strip-unknown-fields loop below
    // would delete it (and the API DTO uses camelCase), so translate now.
    // Empty string → undefined: a deliberate "clear" by the user, sent as
    // null to the server so the field can be unset.
    if ('metadata_schema_guid' in apiPatch) {
      const v = apiPatch.metadata_schema_guid;
      apiPatch.metadataSchemaGuid =
        typeof v === 'string' && v.trim() ? v : null;
      delete apiPatch.metadata_schema_guid;
    }

    // DEV-913 — Owner change → communityId translation.
    //
    // The frontend models ownership as (owner_type, owner_guid) for display
    // in the Project Settings modal, but the backend (per DEV-877's locked
    // design) stores ownership as projects.community_id — the leaf community
    // owns the project, and primary_owner_org_guid is auto-derived server-
    // side via walk_org_root(community_id) on every Update.
    //
    // Without this translation, the strip-unknown-fields loop below would
    // delete owner_type / owner_guid (they're not in the API surface and
    // not in knownApiFields), and the user's owner change would silently
    // fail to persist — exactly Skip's repro.
    //
    // Translation rules:
    //   owner_type='community' + owner_guid=X → communityId = X
    //   owner_type='user'                     → no-op for now (workshop case)
    //
    // The user/workshop case is deferred (filed separately) — it needs the
    // user's personal_owner_user_guid surfaced on CommunityEntityResponse
    // before the frontend can resolve the workshop community to assign.
    // For now, picking "Me" in the dropdown writes only to the local store.
    // The community-transfer case (the demo-blocking one) works fully.
    //
    // owner_type / owner_guid stay in the local override store for display
    // purposes — only the API patch is translated.
    if ('owner_type' in apiPatch || 'owner_guid' in apiPatch) {
      const ownerType = apiPatch.owner_type;
      const ownerGuid = apiPatch.owner_guid;
      if (ownerType === 'community' && typeof ownerGuid === 'string' && ownerGuid.trim()) {
        apiPatch.communityId = ownerGuid;
      }
      // owner_type === 'user' deliberately falls through with no API write.
      // The workshop-resolver follow-up will fill this in.
      delete apiPatch.owner_type;
      delete apiPatch.owner_guid;
    }

    // Strip fields the API doesn't recognize to avoid noise in the request.
    // The backend UpdateProjectRequest only accepts camelCase fields.
    const knownApiFields = new Set([
      'name', 'description', 'status', 'region', 'communityId',
      'projectCategory', 'projectStatus', 'contentStatus', 'projectReach',
      'startDate', 'finishDate', 'budgetEstimate', 'budgetCurrency', 'contingencyPercent',
      'locationLat', 'locationLon',
      'communityL1', 'communityL2', 'communityL3', 'communityDisplay',
      'primaryLocationEntityGuid',
      'heroImageUrl', 'cardImageUrl', 'logoUrl',
      'reportingCadence',
      'scheduleStartDate',
      'ganttColorTheme',
      'workWeek',
      'categoryWeights',
      // DEV-916: project metadata-schema attachment. Translated above
      // from snake_case `metadata_schema_guid`.
      'metadataSchemaGuid',
    ]);
    for (const k of Object.keys(apiPatch)) {
      if (!knownApiFields.has(k)) delete apiPatch[k];
    }

    updateProject(guid, apiPatch).catch((err) => {
      console.warn("[projectConfigOverrides] API write failed:", err);
    });
  }
};

export const clearProjectOverride = (project_guid: GUID): void => {
  ensureWarm();
  const guid = String(project_guid);
  const guidUp = guid.toUpperCase();

  // Case-insensitive key resolution
  let keyToDelete: string | undefined;
  projectConfigStore.forEach((_, key) => {
    if (key.toUpperCase() === guidUp) keyToDelete = key;
  });

  if (!keyToDelete) keyToDelete = guidUp;
  projectConfigStore.delete(keyToDelete);

  try {
    window.dispatchEvent(new CustomEvent("l4e_project_config_changed"));
  } catch {
    // ignore
  }
};

const applyOwnerBackCompat = (merged: any, override: ProjectConfigOverride) => {
  // Preferred fields
  if (override.owner_type !== undefined) merged.owner_type = override.owner_type;
  if (override.owner_guid !== undefined) merged.owner_guid = override.owner_guid;

  // If legacy `owning_community_entity_guid` exists and owner_* fields are not set,
  // map it to owner_type=community.
  const hasOwner = merged.owner_type || merged.owner_guid;
  if (!hasOwner && override.owning_community_entity_guid !== undefined) {
    merged.owner_type = "community";
    merged.owner_guid = override.owning_community_entity_guid;
  }

  // Also keep legacy field in sync for older UI paths.
  if (override.owning_community_entity_guid !== undefined) {
    merged.owning_community_entity_guid = override.owning_community_entity_guid;
  } else if (merged.owner_type === "community" && merged.owner_guid) {
    merged.owning_community_entity_guid = merged.owner_guid;
  }
};

export const mergeProjectWithOverride = <T extends Record<string, any>>(
  project: T,
  override: ProjectConfigOverride | null
): T => {
  if (!override) return project;
  const merged: any = { ...project };

  if (override.project_name !== undefined) merged.project_name = override.project_name;
  if (override.description !== undefined) merged.description = override.description;
  if (override.project_start_date !== undefined) (merged as any).project_start_date = override.project_start_date;
  if (override.project_end_date !== undefined) (merged as any).project_end_date = override.project_end_date;
  if (override.status !== undefined) merged.status = override.status;
  if (override.size !== undefined) merged.size = override.size;

  if (override.project_category !== undefined) merged.project_category = override.project_category;
  if (override.project_status !== undefined) merged.project_status = override.project_status;
  if (override.content_status !== undefined) merged.content_status = override.content_status;

  applyOwnerBackCompat(merged, override);

  if (override.primary_location_entity_guid !== undefined)
    merged.primary_location_entity_guid = override.primary_location_entity_guid;

  if (override.location_lat !== undefined) merged.location_lat = override.location_lat;
  if (override.location_lon !== undefined) merged.location_lon = override.location_lon;

  // Keep derived community grouping fields in sync with primary location.
  // (KPIGridTable grouping uses community_l1/l2/l3, not the raw GUID.)
  if (override.primary_location_entity_guid !== undefined && merged.primary_location_entity_guid) {
    const names = resolveCommunityPathNames(String(merged.primary_location_entity_guid));
    if (names.length > 0) {
      (merged as any).community_l1 = names[0] ?? (merged as any).community_l1;
      (merged as any).community_l2 = names[1] ?? "";
      (merged as any).community_l3 = names[2] ?? "";
      (merged as any).Community = names[names.length - 1] ?? (merged as any).Community;
    }
  }

  if (override.visibility !== undefined) merged.visibility = override.visibility;
  if (override.public_scope !== undefined) merged.public_scope = override.public_scope;
  if (override.is_hidden !== undefined) merged.is_hidden = override.is_hidden;
  if (override.metadata_schema_guid !== undefined) merged.metadata_schema_guid = override.metadata_schema_guid;
  if (override.kpi_profile_guid !== undefined) merged.kpi_profile_guid = override.kpi_profile_guid;
  if (override.logo_url !== undefined) merged.logo_url = override.logo_url;
  if (override.tile_image_url !== undefined) {
    merged.tile_image_url = override.tile_image_url;
    // Keep hero_image_url in sync — tile_image_url is the canonical main hero override
    merged.hero_image_url = override.tile_image_url;
  }
  if (override.card_image_url !== undefined) merged.card_image_url = override.card_image_url;
  if (override.tags !== undefined) merged.tags = override.tags;
  if (override.parent_project_guid !== undefined) merged.parent_project_guid = override.parent_project_guid;
  if (override.reporting_cadence !== undefined) merged.reporting_cadence = override.reporting_cadence;
  if (override.audit_enabled !== undefined) merged.audit_enabled = override.audit_enabled;
  if (override.schedule_start_date !== undefined) merged.schedule_start_date = override.schedule_start_date;
  if (override.work_week !== undefined) merged.work_week = override.work_week;
  if (override.contingency_percent !== undefined) merged.contingency_percent = override.contingency_percent;

  return merged;
};

// --- Community entity path resolution (guid -> [l1,l2,l3] names) ---
// Maps are initialized from static seed data and refreshed with API data
// when refreshCommunityMaps() is called (typically after API fetch in App).

let __entityNameByGuid: Record<string, string> = (() => {
  const m: Record<string, string> = {};
  try {
    for (const e of communityEntities as any[]) {
      const g = String((e as any)?.entity_guid ?? "");
      if (!g) continue;
      m[g] = String((e as any)?.name ?? "");
    }
  } catch {
    // ignore
  }
  return m;
})();

let __parentByChild: Record<string, string> = (() => {
  const m: Record<string, string> = {};
  try {
    for (const r of communityEntityRelationships as any[]) {
      const child = String((r as any)?.child_entity_guid ?? "");
      const parent = String((r as any)?.parent_entity_guid ?? "");
      if (child && parent) m[child] = parent;
    }
  } catch {
    // ignore
  }
  return m;
})();

/**
 * Refresh the community entity maps with fresh data (typically from the API).
 * After calling this, any subsequent resolveCommunityPathNames() calls will
 * use the new data, so newly-created sub-communities are recognized.
 *
 * @param entities  Array of objects with { entity_guid, name } (seed-data shape)
 * @param relationships  Array of objects with { child_entity_guid, parent_entity_guid }
 */
export function refreshCommunityMaps(
  entities: any[],
  relationships: any[],
): void {
  const nameMap: Record<string, string> = {};
  if (Array.isArray(entities)) {
    for (const e of entities) {
      const g = String(e?.entity_guid ?? "").trim();
      if (!g) continue;
      nameMap[g] = String(e?.name ?? "");
    }
  }
  const parentMap: Record<string, string> = {};
  if (Array.isArray(relationships)) {
    for (const r of relationships) {
      const child = String(r?.child_entity_guid ?? "").trim();
      const parent = String(r?.parent_entity_guid ?? "").trim();
      if (child && parent) parentMap[child] = parent;
    }
  }
  // Merge: API data takes precedence, but keep static entries that may not be in DB yet.
  __entityNameByGuid = { ...__entityNameByGuid, ...nameMap };
  __parentByChild = { ...__parentByChild, ...parentMap };

  // Signal consumers that resolve names off these maps (e.g. the resource
  // sharing checklist) so they can recompute once the maps populate. Without
  // this, a component whose memo ran before the maps filled stays stuck on its
  // GUID fallback (community names never appear).
  try {
    window.dispatchEvent(new CustomEvent('l4e_community_maps_changed'));
  } catch { /* SSR / no window */ }
}

export function resolveCommunityPathNames(entityGuid: string): string[] {
  const guid = String(entityGuid || "").trim();
  if (!guid) return [];

  // Case-insensitive lookup helpers — GUIDs from different sources may have
  // inconsistent casing (e.g. API returns lowercase, seed data uppercase).
  const findName = (g: string): string | undefined =>
    __entityNameByGuid[g] ?? __entityNameByGuid[g.toLowerCase()] ?? __entityNameByGuid[g.toUpperCase()];
  const findParent = (g: string): string | undefined =>
    __parentByChild[g] ?? __parentByChild[g.toLowerCase()] ?? __parentByChild[g.toUpperCase()];

  const chain: string[] = [];
  const seen = new Set<string>();
  let cur: string | undefined = guid;

  while (cur && !seen.has(cur.toLowerCase()) && chain.length < 10) {
    seen.add(cur.toLowerCase());
    chain.push(cur);
    cur = findParent(cur);
  }

  const names = chain
    .slice()
    .reverse()
    .map((g) => findName(g))
    .filter((n) => typeof n === "string" && n.trim().length > 0)
    // Strip "Earth" root — it consumes an l1 slot and pushes cities out of the
    // 3-level window.  We don't need a planet-level grouping at this stage.
    .filter((n) => n!.toLowerCase() !== "earth");

  // Return at most 3 levels for community_l1/l2/l3.
  return names.slice(0, 3);
}

/**
 * DEV-1368 (Skip 2026-06-22). GUID-bearing sibling of
 * resolveCommunityPathNames — returns the ancestor chain as
 * { guid, name } pairs (root-to-leaf, Earth stripped) so a caller can
 * render each hop as a link to its own community page. Used by the
 * Action Center role-detail header to show a clickable geo path under
 * the project name. Same walk/cap/casing semantics as the names helper.
 */
export function resolveCommunityPath(entityGuid: string): Array<{ guid: string; name: string }> {
  const guid = String(entityGuid || "").trim();
  if (!guid) return [];

  const findName = (g: string): string | undefined =>
    __entityNameByGuid[g] ?? __entityNameByGuid[g.toLowerCase()] ?? __entityNameByGuid[g.toUpperCase()];
  const findParent = (g: string): string | undefined =>
    __parentByChild[g] ?? __parentByChild[g.toLowerCase()] ?? __parentByChild[g.toUpperCase()];

  const chain: string[] = [];
  const seen = new Set<string>();
  let cur: string | undefined = guid;
  while (cur && !seen.has(cur.toLowerCase()) && chain.length < 10) {
    seen.add(cur.toLowerCase());
    chain.push(cur);
    cur = findParent(cur);
  }

  return chain
    .slice()
    .reverse()
    .map((g) => ({ guid: g, name: findName(g) || "" }))
    .filter((p) => p.name.trim().length > 0 && p.name.toLowerCase() !== "earth");
}

/**
 * DEV-1025. Format a community's full ancestor path as a single
 * leaf-first, comma-separated label for display in notification
 * subjects, engagement tiles, and any other surface where users
 * need to disambiguate communities with similar names.
 *
 * Examples (with the 3-level cap from resolveCommunityPathNames):
 *   geo_city Newport          → "Newport, Rhode Island, United States"
 *   org_rotary_club Pawtucket → "Pawtucket Rotary Club, Rotary International"
 *   organization Rotary Intl  → "Rotary International"
 *   unknown / empty           → "" (caller should fall back)
 *
 * Returns empty string when the GUID can't be resolved so callers
 * can chain a fallback (resolveName / bare entity name / generic
 * label) the same way they would for any other resolver.
 */
export function formatCommunityPathLabel(entityGuid: string): string {
  const guid = String(entityGuid || "").trim();
  if (!guid) return "";

  // DEV-1164 fix 2026-05-31: walk the chain leaf-first ourselves rather
  // than reusing resolveCommunityPathNames. That helper does .slice(0, 3)
  // which keeps the TOP 3 levels (root-to-leaf order) — fine for chains
  // ≤ 3 deep (most geo entities) but it CUTS OFF THE LEAF for any chain
  // 4+ deep (e.g., org_rotary_club Hampton nested under District 7780 →
  // Zone 32 → Rotary International). Result: Skip's "Club Secretary" card
  // was being labeled "District 7780, Zone 32, Rotary International" with
  // his actual club (Hampton Rotary Club) silently dropped.
  //
  // For display purposes we always want the LEAF + up to 2 ancestors,
  // joined leaf-first. "Hampton Rotary Club, District 7780, Zone 32"
  // reads correctly; the root (Rotary International) is fine to omit
  // because the leaf carries enough disambiguation context.
  const findName = (g: string): string | undefined =>
    __entityNameByGuid[g] ?? __entityNameByGuid[g.toLowerCase()] ?? __entityNameByGuid[g.toUpperCase()];
  const findParent = (g: string): string | undefined =>
    __parentByChild[g] ?? __parentByChild[g.toLowerCase()] ?? __parentByChild[g.toUpperCase()];

  const labels: string[] = [];
  const seen = new Set<string>();
  let cur: string | undefined = guid;

  while (cur && !seen.has(cur.toLowerCase()) && labels.length < 3) {
    seen.add(cur.toLowerCase());
    const name = findName(cur);
    if (name && name.toLowerCase() !== "earth") {
      labels.push(name);
    }
    cur = findParent(cur);
  }

  return labels.join(", ");
}

/**
 * Resolve the full ancestor chain as { guid, name } pairs.
 * Returns root-to-leaf order, excluding "Earth" and the entity itself.
 * Used for breadcrumb navigation where GUIDs are needed for linking.
 */
export function resolveCommunityAncestors(entityGuid: string): { guid: string; name: string }[] {
  const guid = String(entityGuid || "").trim();
  if (!guid) return [];

  const findName = (g: string): string | undefined =>
    __entityNameByGuid[g] ?? __entityNameByGuid[g.toLowerCase()] ?? __entityNameByGuid[g.toUpperCase()];
  const findParent = (g: string): string | undefined =>
    __parentByChild[g] ?? __parentByChild[g.toLowerCase()] ?? __parentByChild[g.toUpperCase()];

  const chain: string[] = [];
  const seen = new Set<string>();
  let cur: string | undefined = guid;

  while (cur && !seen.has(cur.toLowerCase()) && chain.length < 10) {
    seen.add(cur.toLowerCase());
    chain.push(cur);
    cur = findParent(cur);
  }

  // Root-to-leaf, exclude Earth and the entity itself
  return chain
    .slice()
    .reverse()
    .map((g) => ({ guid: g, name: findName(g) || '' }))
    .filter((n) => n.name.toLowerCase() !== 'earth' && n.guid.toLowerCase() !== guid.toLowerCase())
    .filter((n) => n.name.trim().length > 0);
}

/**
 * Re-derive community_l1 / l2 / l3 for every project that has a
 * primary_location_entity_guid, using the current (possibly API-refreshed) maps.
 * Call this after refreshCommunityMaps() to ensure geography grouping picks up
 * newly-created sub-communities.
 */
export const rederiveCommunityFields = <T extends Record<string, any>>(projects: T[]): T[] => {
  return projects.map((p) => {
    const locGuid = String((p as any).primary_location_entity_guid ?? "").trim();
    if (!locGuid) return p;
    const names = resolveCommunityPathNames(locGuid);
    if (names.length === 0) return p;
    return {
      ...p,
      community_l1: names[0] ?? (p as any).community_l1 ?? "",
      community_l2: names[1] ?? "",
      community_l3: names[2] ?? "",
      Community: names[names.length - 1] ?? (p as any).Community ?? "",
    };
  });
};

export const mergeProjectsWithOverrides = <T extends Record<string, any>>(projects: T[]): T[] => {
  const map = getProjectOverrides();
  if (!map || Object.keys(map).length === 0) return projects;

  // Build case-insensitive lookup once.
  const mapUpper: Record<string, any> = {};
  for (const k of Object.keys(map)) mapUpper[k.toUpperCase()] = map[k];

  return projects.map((p) => {
    const guid = String((p as any).project_GUID ?? (p as any).project_guid ?? "");
    if (!guid) return p;
    const patch = mapUpper[guid.toUpperCase()] || map[guid];
    if (!patch) return p;
    return mergeProjectWithOverride(p, { project_guid: guid as any, ...(patch as any) });
  });
};

export const getProjectDisabledWorkflowActionTypes = (projectGuid: string): string[] => {
  const map = getProjectOverrides();
  const pg = String(projectGuid || "");
  const pgUp = pg.toUpperCase();
  const key = Object.keys(map).find((k) => k.toUpperCase() === pgUp) || pg;
  const ov = (map as any)?.[key];
  const arr = (ov as any)?.disabled_workflow_action_types;
  return Array.isArray(arr) ? arr.map((x) => String(x)) : [];
};

export const isProjectWorkflowEnabled = (projectGuid: string, actionType: string): boolean => {
  const disabled = new Set(getProjectDisabledWorkflowActionTypes(projectGuid));
  return !disabled.has(String(actionType || ""));
};

export const setProjectWorkflowEnabled = (projectGuid: string, actionType: string, enabled: boolean): void => {
  const pg = String(projectGuid || "");
  const at = String(actionType || "");
  if (!pg || !at) return;

  const current = new Set(getProjectDisabledWorkflowActionTypes(pg));
  if (enabled) current.delete(at);
  else current.add(at);

  setProjectOverride(pg, {
    disabled_workflow_action_types: Array.from(current.values()).sort(),
  });
};
